import styled, { useTheme } from 'styled-components'
import { useRef, useState, useEffect } from 'react'
import { useGetSize } from 'Hooks/useGetSize'
import * as d3 from 'd3'
import { Small, Label, Tick, ContentWidth, ChartTitle } from 'theme/styles'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { useSpring, animated } from '@react-spring/web'
import { AnimatedNote } from 'components/AnimatedNote'

const Arrow = ({
  x1,
  x2,
  y,
  value,
}: {
  x1: number
  x2: number
  y: number
  value: number
}) => {
  const theme = useTheme()
  const strokeWidth = 2
  const dir = x1 - x2 < 0 ? 1 : -1
  const head = dir === 1 ? x1 : x2
  const tail = dir === 1 ? x2 : x1
  const circleRadius = 8
  const isMobile = window.innerWidth < 768

  return (
    <g>
      {!isMobile && (
        <g>
          {x2 - x1 > 20 && (
            <line
              x1={head + circleRadius + 2}
              y1={y}
              x2={tail - circleRadius - 2}
              y2={y}
              stroke={`rgb(${theme.backgroundColorRGB})`}
              strokeWidth={strokeWidth + 2}
              strokeLinecap='round'
            />
          )}

          <polyline
            points={`${4 * dir},${4 * dir} ${0},${0} ${4 * dir},${-4 * dir}`}
            stroke={`rgb(${theme.backgroundColorRGB})`}
            strokeWidth={strokeWidth + 2}
            strokeLinecap='round'
            fill='transparent'
            style={{
              transform: `translate(${x1 + circleRadius + 2}px, ${y}px)`,
            }}
          />

          {x2 - x1 > 20 && (
            <line
              x1={head + circleRadius + 2}
              y1={y}
              x2={tail - circleRadius - 2}
              y2={y}
              stroke={`rgb(${theme.yellowColor})`}
              strokeWidth={strokeWidth}
              strokeLinecap='round'
            />
          )}

          <polyline
            points={`${4 * dir},${4 * dir} ${0},${0} ${4 * dir},${-4 * dir}`}
            stroke={
              dir === 1
                ? `rgb(${theme.yellowColor})`
                : `rgb(${theme.yellowColor})`
            }
            strokeWidth={strokeWidth}
            strokeLinecap='round'
            fill='transparent'
            style={{
              transform: `translate(${x1 + circleRadius + 2}px, ${y}px)`,
            }}
          />
        </g>
      )}

      <g
        style={{
          transform: `translate(${
            x2 < window.innerWidth * 0.85 ? x2 + 15 : x1 - 35
          }px, ${y}px)`,
        }}
      >
        <text
          x={0}
          y={0}
          fontSize='0.75em'
          textAnchor={dir === 1 ? 'start' : 'end'}
          alignmentBaseline='middle'
          paintOrder='stroke'
          stroke={theme.backgroundColor}
          strokeWidth={9}
          strokeLinecap='butt'
          strokeLinejoin='round'
          fill='var(--text-color)'
        >
          {value > 0 ? `-${value.toFixed(0)}` : `${value.toFixed(2)}`}
        </text>
      </g>
    </g>
  )
}

const xTicksLabels: { [key: number]: string } = {
  50: '19 fév.',
  100: '10 avr.',
  150: '30 mai',
  200: '19 juill.',
  250: '7 sept.',
}

const SpeciesComparison = () => {
  //const { breakpoints } = useTheme()
  //const isMobile = useMediaQuery(breakpoints.only('md'))
  const theme = useTheme()
  const [data, setData] = useState<any>([])
  const circleRadius = 8

  useEffect(() => {
    d3.csv<any>(
      `${
        process.env.NODE_ENV === 'production'
          ? 'https://dev-interaktiv-mf.tagesanzeiger.ch/2023/seasons'
          : ''
      }/arrows-data.csv`,
      (d: any) => {
        return {
          species: d['species'],
          meanA: +d['current_gen'],
          meanB: +d['previous_gen'],
          meanC: +d['oldest_gen'],
        }
      }
    )
      .then((data: any) => {
        console.log(data)
        setData(data)
      })
      .catch((error: any) => {
        console.error('Error fetching the CSV data:', error)
      })
  }, [])

  const isMobile = window.innerWidth < 768
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const { width } = useGetSize(containerRef)
  const height = isMobile ? width * 1.65 : width * 0.75

  const xDomain = isMobile ? [20, 260] : [40, 260]
  const yDomain = [0, 18]
  const margin = {
    top: 30,
    right: 10,
    bottom: 40,
    left: 80,
  }
  const tickXLength = 10

  const xScale = d3
    .scaleLinear()
    .domain(xDomain as any)
    .range([margin.left, width - margin.right])

  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .range([height - margin.bottom, margin.top])

  const xTicks = isMobile ? [100, 150, 200] : [50, 100, 150, 200, 250]

  return (
    <Container>
      <ContentWidth ref={containerRef}>
        <Legend>
          <LegendBloc>
            <LegendCircle
              style={{ background: `rgba(${theme.yellowColor}, 1)` }}
            />{' '}
            2000—2024
          </LegendBloc>
          <LegendBloc>
            <LegendCircle
              style={{ background: `rgba(${theme.yellowColor}, 0.65)` }}
            />{' '}
            1975—2000
          </LegendBloc>
          <LegendBloc>
            <LegendCircle
              style={{ background: `rgba(${theme.yellowColor}, 0.3)` }}
            />{' '}
            1950—1975
          </LegendBloc>
        </Legend>

        <Svg ref={svgRef} width={width} height={height}>
          {/* Y grid */}
          {/* {xTicks.map((value) => (
          <GridLine
            x1={xScale(value)}
            x2={xScale(value)}
            y1={margin.top}
            y2={height - margin.bottom}
          />
        ))} */}

          {/* X ticks */}
          {xTicks.map((value) => (
            <Tick
              x1={xScale(value)}
              x2={xScale(value)}
              y1={height - margin.bottom + 15 + tickXLength}
              y2={height - margin.bottom + 15}
            />
          ))}

          {/* X grid */}
          {data.map((value: any, i: number) => {
            return (
              <GridLine
                x1={115}
                x2={width - margin.right}
                y1={yScale(i)}
                y2={yScale(i)}
              />
            )
          })}

          {data.map((value: any, i: number) => {
            return (
              <g>
                <Circle
                  cx={xScale(value.meanA)}
                  cy={yScale(i)}
                  r={circleRadius}
                  fill={`rgb(${theme.yellowColor})`}
                  opacity={1}
                />
                <Circle
                  cx={xScale(value.meanB)}
                  cy={yScale(i)}
                  r={circleRadius}
                  fill={`rgb(${theme.yellowColor})`}
                  opacity={0.65}
                />
                <Circle
                  cx={xScale(value.meanC)}
                  cy={yScale(i)}
                  r={circleRadius}
                  fill={`rgb(${theme.yellowColor})`}
                  opacity={0.3}
                />
              </g>
            )
          })}

          {data.map((value: any, i: number) => {
            return (
              <g>
                <Arrow
                  x1={xScale(value.meanA)}
                  x2={xScale(value.meanC)}
                  y={yScale(i)}
                  value={value.meanC - value.meanA}
                />
              </g>
            )
          })}
        </Svg>

        {/* Y labels */}
        {data.map((value: any, i: number) => (
          <NameLabel
            style={{
              top: yScale(i),
              left: 0,
              textAlign: 'right',
            }}
          >
            {value.species}
          </NameLabel>
        ))}

        {/* X labels */}
        {xTicks.map((value) => (
          <Label style={{ left: xScale(value), top: height - 6 }}>
            {xTicksLabels[value]}
          </Label>
        ))}

        <AnimatedNote
          chartDimensions={[width, height]}
          show={true}
          arrowHeadposition={[xScale(145), yScale(5.2)]}
          textPosition={[xScale(170), yScale(7)]}
        >
          La marguerite fleurit en moyenne <strong>9 jours</strong> plus tôt.
        </AnimatedNote>

        <ChartTitle
          style={{
            top: 0,
            left: 0,
            transform: 'translateX(0)',
            textAlign: 'left',
            letterSpacing: 0.85,
            width: 250,
          }}
        >
          <strong>Date de fleurissement</strong>
        </ChartTitle>
      </ContentWidth>
      <TextWidth>
        <Small>
          <strong>Source</strong>: MétéoSuisse
        </Small>
      </TextWidth>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  margin: 60px 0 90px 0;
`

const GridLine = styled.line`
  // stroke: ${({ theme }) => `rgba(${theme.textColorOnBackground}, 0.1)`};
  stroke: var(--text-color);
  opacity: 0.1;
  stroke-width: 1px;
`

const Circle = styled.circle`
  stroke: ${({ theme }) => `${theme.backgroundColor}`};
  stroke-width: 1px;
  transition: all 1s ease;
`

const Svg = styled.svg`
  display: block;
  position: relative;
  z-index: 1;
`

const NameLabel = styled(Label)`
  width: 100px;
  transform: translateY(-50%);
`

const LegendCircle = styled.div`
  margin-right: 5px;
  display: inline-block;
  width: 20px;
  height: 20px;
  top: 0;
  left: 0;
  border-radius: 20px;
`

const Legend = styled.div`
  position: absolute;
  top: 5vmin;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.textS};
`

const LegendBloc = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  transition: all 0.4s ease-in-out;
  overflow: hidden;

  &:last-child {
    margin-right: 0;
  }
`

export default SpeciesComparison
